import { failureSaid } from "../localization/failure.ts";
import { say } from "../localization/say.ts";
import { act, notSignedIn, Refused, read } from "../net/rally.ts";

type Key = { publicKey: string };

const DEVICE = "rally-push-device";

export function bytesOf(said: string): Uint8Array {
  const padded = said.replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export function base64UrlOf(bytes: ArrayBuffer | null): string {
  if (!bytes) return "";
  let raw = "";
  for (const byte of new Uint8Array(bytes)) raw += String.fromCharCode(byte);
  return btoa(raw).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function thisDevice(): string {
  try {
    const held = localStorage.getItem(DEVICE);
    if (held) return held;
    const made = `${aBrowser()} ${Math.random().toString(36).slice(-6)}`;
    localStorage.setItem(DEVICE, made);
    return made;
  } catch {
    return aBrowser();
  }
}

function aBrowser(): string {
  const said = typeof navigator === "undefined" ? "" : navigator.userAgent;
  const name = /Firefox/.test(said)
    ? "Firefox"
    : /Edg\//.test(said)
      ? "Edge"
      : /Chrome|Chromium/.test(said)
        ? "Chrome"
        : /Safari/.test(said)
          ? "Safari"
          : "a browser";
  const on = /iPhone|iPad/.test(said)
    ? "iOS"
    : /Android/.test(said)
      ? "Android"
      : /Mac OS X/.test(said)
        ? "macOS"
        : /Windows/.test(said)
          ? "Windows"
          : /Linux/.test(said)
            ? "Linux"
            : "";
  return on ? `${name} on ${on}` : name;
}

export type TurnedOn =
  | { on: true }
  | {
      on: false;
      why: "denied" | "cannot" | "signedOut" | "refused" | "pushService" | "failed";
      said?: string;
    };

function saidBy(_cause: unknown): string {
  return say("failure.generic");
}

export async function turnOnNotifications(): Promise<TurnedOn> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
    return { on: false, why: "cannot" };
  }
  const said = await Notification.requestPermission();
  if (said !== "granted") return { on: false, why: "denied" };
  try {
    const worker = await navigator.serviceWorker.ready;
    const key = await read<Key>("push.key");
    const already = await worker.pushManager.getSubscription();

    let subscription: PushSubscription;
    try {
      subscription =
        already && base64UrlOf(already.options.applicationServerKey ?? null) === key.publicKey
          ? already
          : await mint(worker, already, key.publicKey);
    } catch (cause) {
      return { on: false, why: "pushService", said: saidBy(cause) };
    }
    await act("push.subscribe", {
      device: thisDevice(),
      endpoint: subscription.endpoint,
      p256dh: base64UrlOf(subscription.getKey("p256dh")),
      auth: base64UrlOf(subscription.getKey("auth")),
    });
    return { on: true };
  } catch (cause) {
    if (notSignedIn(cause)) return { on: false, why: "signedOut" };
    if (cause instanceof Refused) {
      return { on: false, why: "refused", said: failureSaid(cause) };
    }
    return { on: false, why: "failed", said: saidBy(cause) };
  }
}

export function whyItDidNot(said: TurnedOn): string | null {
  if (said.on || said.why === "denied" || said.why === "cannot") return null;
  if (said.why === "signedOut") return say("push.signedOut");
  if (said.why === "refused") return say("push.refused")(said.said ?? "");
  if (said.why === "pushService") return say("push.pushService")(said.said ?? "");
  return say("push.failed");
}

async function mint(
  worker: ServiceWorkerRegistration,
  already: PushSubscription | null,
  publicKey: string,
): Promise<PushSubscription> {
  if (already) await already.unsubscribe().catch(() => false);

  return worker.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: bytesOf(publicKey) as BufferSource,
  });
}

export async function turnOffNotifications(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  const worker = await navigator.serviceWorker.ready;
  const subscription = await worker.pushManager.getSubscription();
  if (!subscription) return;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe().catch(() => false);
  await act("push.forget", { endpoint }).catch(() => undefined);
}

export async function subscribedHere(): Promise<boolean> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
  try {
    const worker = await navigator.serviceWorker.ready;
    return (await worker.pushManager.getSubscription()) !== null;
  } catch {
    return false;
  }
}
